import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getApiUrl } from "../../../utilities/consts";
import { useAuth0 } from "@auth0/auth0-react";
import ScheduleDays from "./ScheduleDays";
import ScheduleTimes from "./ScheduleTimes";
import SchedulePrograms from "./SchedulePrograms";
import CurrentTimeIndicator from "./CurrentTimeIndicator";

export default function ChannelSchedule({ channelID }) {
    const { getAccessTokenSilently } = useAuth0();

    /** @type {[Program[], React.Dispatch<React.SetStateAction<Program[]>>]} */
    const [loading, setLoading] = useState(true);
    const [scheduleNotAvailable, setScheduleNotAvailable] = useState(false);
    const [scheduleData, setScheduleData] = useState([]);
    const scheduleStartTime =
        scheduleData.length > 0 ? scheduleData[0].start : new Date(0);
    const scheduleEndTime =
        scheduleData.length > 0
            ? scheduleData[scheduleData.length - 1].stop
            : new Date(0);
    const scheduleTotalTime = scheduleEndTime - scheduleStartTime;

    const [currentTime, setCurrentTime] = useState(new Date());

    // update current time
    useEffect(() => {
        const interval = window.setInterval(() => {
            const newTime = new Date();
            setCurrentTime((prevTime) => {
                if (newTime.getMinutes() !== prevTime.getMinutes())
                    return newTime;
                return prevTime;
            });
        }, 1000);
        return () => window.clearInterval(interval);
    }, [currentTime]);

    // fetch schedule data
    useEffect(() => {
        (async () => {
            const accessToken = await getAccessTokenSilently({
                authorizationParams: { scope: "read:channels" },
            });
            const res = await fetch(
                `${getApiUrl()}/channel/${channelID}/schedule?startDateTime=${
                    new Date().toISOString().split("T")[0]
                }T00:00Z&daysRequested=3`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );
            const json = await res.json();
            if (!res.ok) {
                if (res.status === 400) {
                    setLoading(false);
                    setScheduleNotAvailable(true);
                }
                console.error("Error fetching schedule:", json);
                return;
            }
            const parsedData = json.map((program) => ({
                ...program,
                start: new Date(program.start),
                stop: new Date(program.stop),
            }));
            setScheduleData(parsedData);
            setLoading(false);
        })();
    }, [channelID, getAccessTokenSilently]);

    if (loading) return <CircularProgress />;

    if (scheduleNotAvailable)
        return (
            <Typography>
                The schedule is not available for this station.
            </Typography>
        );

    return (
        <Box
            id="ChannelScheduleScrollBox"
            sx={{
                width: "100vw",
                height: "62vh",
                overflowY: "scroll",
                display: "flex",
                justifyContent: "center",
                "@media (max-height: 895px)": {
                    height: "inherit",
                },
            }}
        >
            <Stack
                id="ChannelScheduleContainer"
                direction="row"
                height={`${(scheduleTotalTime / 1000 / 60 / 60) * 10}em`} // 10em per minute
                sx={{
                    width: "100%",
                    marginTop: "1em",
                    marginBottom: "1em",
                    "@media (max-width: 750px)": {
                        marginTop: "1.5em",
                        marginBottom: "1.5em",
                    },
                }}
            >
                <ScheduleDays
                    scheduleStartTime={scheduleStartTime}
                    scheduleEndTime={scheduleEndTime}
                />
                <ScheduleTimes
                    scheduleStartTime={scheduleStartTime}
                    scheduleEndTime={scheduleEndTime}
                />
                <SchedulePrograms
                    scheduleData={scheduleData}
                    currentTime={currentTime}
                    scheduleTotalTime={scheduleTotalTime}
                />
                <CurrentTimeIndicator
                    currentTime={currentTime}
                    scheduleStartTime={scheduleStartTime}
                    scheduleEndTime={scheduleEndTime}
                />
            </Stack>
        </Box>
    );
}
